import { useEffect, useState, useCallback } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError, API } from "@/lib/api";
import { StatusBadge } from "@/pages/Dashboard";
import { FileArrowDown, Eye } from "@phosphor-icons/react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const STATUS_OPTIONS = ["submitted", "under_review", "approved", "ready", "rejected"];

export default function AdminDashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [apps, setApps] = useState([]);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [remark, setRemark] = useState("");
  const [docs, setDocs] = useState([]);

  const load = useCallback(() => {
    const q = filter === "all" ? "" : `?status=${filter}`;
    api.get(`/admin/applications${q}`).then(({ data }) => setApps(data)).catch(() => {});
    api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/documents").then(({ data }) => setDocs(data)).catch(() => {});
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const openApp = (a) => { setActive(a); setNewStatus(a.status); setRemark(""); };

  const save = async () => {
    try {
      await api.patch(`/admin/applications/${active.app_id}`, { status: newStatus, remark });
      toast.success("Status updated");
      setActive(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const metrics = [
    { k: "Total", v: stats?.total ?? "—" },
    { k: "Citizens", v: stats?.users ?? "—" },
    { k: "In Review", v: stats?.by_status?.under_review ?? 0 },
    { k: "Ready", v: (stats?.by_status?.ready ?? 0) + (stats?.by_status?.approved ?? 0) },
  ];

  return (
    <main className="min-h-screen pt-24">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <span className="font-mono text-xs uppercase tracking-[0.3em] text-primary">Admin Panel</span>
        <h1 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl mt-3">Applications</h1>
        <p className="text-muted-foreground mt-2 text-sm">Signed in as {user?.email}</p>

        {/* Metrics */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
          {metrics.map((m, i) => (
            <motion.div key={m.k} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
              className="border border-border rounded-sm p-6 bg-card" data-testid={`stat-${m.k.toLowerCase()}`}>
              <p className="font-display font-black text-4xl">{m.v}</p>
              <p className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground mt-1">{m.k}</p>
            </motion.div>
          ))}
        </div>

        <Tabs defaultValue="applications" className="mt-10">
          <TabsList className="bg-secondary border border-border rounded-sm">
            <TabsTrigger value="applications" data-testid="tab-applications" className="font-mono text-[11px] uppercase tracking-widest data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-sm">Applications</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents" className="font-mono text-[11px] uppercase tracking-widest data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-sm">Documents</TabsTrigger>
          </TabsList>

          <TabsContent value="applications" className="mt-6">
            {/* Filters */}
            <div className="flex flex-wrap gap-2 mb-6">
              {["all", ...STATUS_OPTIONS].map((s) => (
                <button key={s} data-testid={`filter-${s}`} onClick={() => setFilter(s)}
                  className={`font-mono text-[11px] uppercase tracking-widest px-4 py-2 rounded-sm border transition-colors ${filter === s ? "border-primary text-primary bg-primary/10" : "border-border text-muted-foreground hover:border-muted-foreground"}`}>
                  {s.replace(/_/g, " ")}
                </button>
              ))}
            </div>

            {/* Table */}
            <div className="border border-border rounded-sm overflow-x-auto" data-testid="admin-app-table">
              <table className="w-full text-sm min-w-[720px]">
                <thead>
                  <tr className="border-b border-border bg-secondary">
                    {["App ID", "Applicant", "Service", "Phone", "Status", "Submitted", ""].map((h) => (
                      <th key={h} className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground px-4 py-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {apps.length === 0 ? (
                    <tr><td colSpan={7} className="px-4 py-12 text-center text-muted-foreground">No applications found.</td></tr>
                  ) : apps.map((a) => (
                    <tr key={a.app_id} className="border-b border-border last:border-0 hover:bg-secondary/50 transition-colors">
                      <td className="px-4 py-4 font-mono text-xs text-primary">{a.app_id}</td>
                      <td className="px-4 py-4">{a.full_name}</td>
                      <td className="px-4 py-4">{a.service_type}</td>
                      <td className="px-4 py-4 font-mono text-xs">{a.phone}</td>
                      <td className="px-4 py-4"><StatusBadge status={a.status} /></td>
                      <td className="px-4 py-4 font-mono text-[10px] text-muted-foreground">{new Date(a.created_at).toLocaleDateString()}</td>
                      <td className="px-4 py-4">
                        <button data-testid={`manage-${a.app_id}`} onClick={() => openApp(a)} className="font-mono text-[10px] uppercase tracking-widest text-primary hover:underline">Manage</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="documents" className="mt-6">
            <p className="text-muted-foreground text-sm mb-6">All documents uploaded by clients ({docs.length} total).</p>
            <div className="border border-border rounded-sm overflow-x-auto" data-testid="admin-docs-table">
              <table className="w-full text-sm min-w-[820px]">
                <thead>
                  <tr className="border-b border-border bg-secondary">
                    {["Client", "Document", "File", "Service", "Uploaded", "Actions"].map((h) => (
                      <th key={h} className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-foreground px-4 py-3">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {docs.length === 0 ? (
                    <tr><td colSpan={6} className="px-4 py-12 text-center text-muted-foreground">No documents uploaded yet.</td></tr>
                  ) : docs.map((d) => {
                    const url = `${API}/files/${d.storage_path}?auth=${localStorage.getItem("rds_token")}`;
                    return (
                      <tr key={d.file_id} className="border-b border-border last:border-0 hover:bg-secondary/50 transition-colors" data-testid={`doc-row-${d.file_id}`}>
                        <td className="px-4 py-4">
                          <p className="text-foreground">{d.client_name}</p>
                          <p className="font-mono text-[10px] text-muted-foreground">{d.client_email}</p>
                        </td>
                        <td className="px-4 py-4 text-primary">{d.doc_name || "—"}</td>
                        <td className="px-4 py-4 font-mono text-[11px] text-muted-foreground truncate max-w-[160px]">{d.original_filename}</td>
                        <td className="px-4 py-4">{d.service_type || "—"}</td>
                        <td className="px-4 py-4 font-mono text-[10px] text-muted-foreground">{new Date(d.created_at).toLocaleString()}</td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-2">
                            <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`view-doc-${d.file_id}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 border border-border rounded-sm font-mono text-[10px] uppercase tracking-widest hover:border-primary hover:text-primary transition-colors">
                              <Eye size={13} /> View
                            </a>
                            <a href={url} download={d.original_filename} data-testid={`download-doc-${d.file_id}`}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-primary/10 border border-primary/40 rounded-sm font-mono text-[10px] uppercase tracking-widest text-primary hover:bg-primary/20 transition-colors">
                              <FileArrowDown size={13} /> Download
                            </a>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight">{active?.app_id}</DialogTitle>
          </DialogHeader>
          {active && (
            <div className="space-y-4">
              <div className="text-sm space-y-1 text-muted-foreground">
                <p><span className="text-foreground">{active.full_name}</span> · {active.service_type}</p>
                <p>{active.email} · {active.phone}</p>
                <p>{active.address}</p>
              </div>
              {active.documents?.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {active.documents.map((d) => (
                    <a
                      key={d.file_id}
                      href={`${API}/files/${d.storage_path}?auth=${localStorage.getItem("rds_token")}`}
                      target="_blank" rel="noopener noreferrer"
                      data-testid={`admin-doc-${d.file_id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border rounded-sm font-mono text-[10px] uppercase tracking-widest text-muted-foreground hover:border-primary hover:text-primary transition-colors"
                    >
                      {d.name}
                    </a>
                  ))}
                </div>
              )}
              <div>
                <label className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-2 block">Update status</label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger data-testid="status-select" className="bg-secondary border-border rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {STATUS_OPTIONS.map((s) => <SelectItem key={s} value={s} className="capitalize">{s.replace(/_/g, " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground mb-2 block">Remark</label>
                <textarea data-testid="status-remark" rows={2} value={remark} onChange={(e) => setRemark(e.target.value)}
                  className="w-full bg-secondary border border-border rounded-sm px-4 py-3 text-sm focus:outline-none focus:border-primary" placeholder="Add a note for the applicant" />
              </div>
            </div>
          )}
          <DialogFooter>
            <button data-testid="save-status" onClick={save} className="px-6 py-3 bg-primary text-primary-foreground rounded-sm font-mono text-xs uppercase tracking-widest hover:bg-primary/90 transition-colors">Save update</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
